<script setup lang="ts">
import { useDefaultStore } from "@/store";
import api from "@/api";
import { useToast } from "primevue/usetoast";
import { $t } from "@/i18n";
import { useSessionStore } from "@/store/session";
import type { IStatus, ITag } from "@/api/types";

definePage({
	name: "CreatePost",
	meta: {
		name: "Create Post",
	},
});

const toast = useToast();
const store = useDefaultStore();
const sessionStore = useSessionStore();
const form = reactive({
	pst_title: null as string | null,
	pst_utc_datetime: null as Date | Date[] | null,
	thumbnail: null as File | null,
	poster: null as File | null,
	poster_name: null as string | null,
	sta_code: store.statuses[1],
	pst_content: null as string | null,
	pst_description: null as string | null,
	tags: null as number[] | null,
});
const submitLoading = ref(false);
const posterUploadRef = ref(null);
const thumbnailUploadRef = ref(null);
const postThumbnailUrl = ref<string | null>(null);
const postPosterUrl = ref<string | null>(null);
const firstSentence = ref<string | null>(null);

const isValidLoginForm = computed(() => {
	const hasFields = !!(
		form.sta_code &&
		form.pst_content &&
		form.pst_content.trim()
	);
	if (hasFields) return true;
	return false;
});

function onImageSelect($event, type: string) {
	onImageClear(type);
	const file = $event.files[0];

	if (file.size > 1500000) {
		toast.add({
			severity: "warn",
			summary: $t("app.postForm.warn.imageSize.title"),
			detail: $t("app.postForm.warn.imageSize.msg"),
			life: 3000,
		});
	}

	const previewUrl = URL.createObjectURL(file);

	if (type === "poster") {
		postPosterUrl.value = previewUrl;
		form.poster = file;
	} else if (type === "thumbnail") {
		postThumbnailUrl.value = previewUrl;
		form.thumbnail = file;
	}
}

function onImageClear(type: string) {
	if (type === "poster") {
		postPosterUrl.value = null;
		form.poster = null;
	} else if (type === "thumbnail") {
		postThumbnailUrl.value = null;
		form.thumbnail = null;
	}
}

async function createPost() {
	if (!sessionStore.can("create-post")) {
		toast.add({
			severity: "error",
			summary: $t("app.permissions.createPost.title"),
			detail: $t("app.permissions.createPost.msg"),
			life: 3000,
		});
		return;
	}

	if (!form.pst_title || !form.pst_title.trim()) {
		if (!firstSentence.value || !firstSentence.value.trim()) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.requiredTitle.title"),
				detail: $t("app.postForm.error.requiredTitle.msg"),
				life: 3000,
			});
			return;
		} else {
			form.pst_title = firstSentence.value;
		}
	}

	submitLoading.value = true;

	const formData = new FormData();
	for (const key in form) {
		if (
			key !== "poster" &&
			key !== "thumbnail" &&
			key !== "pst_utc_datetime" &&
			key !== "sta_code" &&
			key !== "pst_description"
		) {
			formData.append(key, (form as Record<string, any>)[key]);
		}
	}

	if (form.pst_description && form.pst_description.trim()) {
		formData.append("pst_description", form.pst_description);
	}

	if (form.tags) {
		formData.append("tag_ids", JSON.stringify(form.tags));
	}

	if (form.poster) {
		formData.append("poster", form.poster);
	}

	if (form.thumbnail) {
		formData.append("thumbnail", form.thumbnail);
	}

	if (form.sta_code) {
		formData.append("sta_code", form.sta_code.code);
	}

	if (form.pst_utc_datetime) {
		const dateString = ref("");
		if (form.pst_utc_datetime && !Array.isArray(form.pst_utc_datetime)) {
			const dateObject = new Date(form.pst_utc_datetime);
			dateString.value = dateObject.toString();
		}

		formData.append(
			"pst_utc_datetime",
			store.formatDatepickerDate(dateString.value),
		);
	}

	try {
		const res = await api.postsAdd(formData);
		toast.add({
			severity: "success",
			summary: $t("app.createPost.success.title"),
			detail: $t("app.createPost.success.msg"),
			life: 3000,
		});
		clearForm();
		console.warn(res.data);
	} catch (err: any) {
		if (err.response.data.message.includes("Token has been blacklisted.")) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.tokenBlacklisted.title"),
				detail: $t("app.auth.error.tokenBlacklisted.msg"),
				life: 3000,
			});
			try {
				await sessionStore.logoutApi();
			} catch (err: any) {
				console.warn(err.message);
			}
		} else if (err.response.data.message.includes("Account is suspended.")) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.accSuspended.title"),
				detail: $t("app.auth.error.accSuspended.msg"),
				life: 3000,
			});
		} else if (
			err.response.data.message.includes("valid iso datetime format")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.dateFormat.title"),
				detail: $t("app.postForm.error.dateFormat.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Unauthorized")) {
			toast.add({
				severity: "error",
				summary: $t("app.permissions.createPost.title"),
				detail: $t("app.permissions.createPost.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Invalid image.")) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.invalidImage.title"),
				detail: $t("app.postForm.error.invalidImage.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Title is required.")) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.requiredTitle.title"),
				detail: $t("app.postForm.error.requiredTitle.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Title must be unique.")) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.uniqueTitle.title"),
				detail: $t("app.postForm.error.uniqueTitle.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Content is required.")) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.requiredContent.title"),
				detail: $t("app.postForm.error.requiredContent.msg"),
				life: 3000,
			});
		} else {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.default.title"),
				detail: $t("app.postForm.error.default.msg"),
				life: 3000,
			});
		}
		console.warn(err.message);
	} finally {
		submitLoading.value = false;
	}
}

function clearForm() {
	form.pst_title = null;
	form.pst_description = null;
	form.pst_utc_datetime = null;
	form.poster_name = null;
	form.sta_code = store.statuses[1];
	form.poster = null;
	form.thumbnail = null;
	form.pst_content = null;
	if (posterUploadRef.value) posterUploadRef.value.clear();
	if (thumbnailUploadRef.value) thumbnailUploadRef.value.clear();
}

const isOptionDisabled = (option: IStatus): boolean => {
	if (!option.permission_create) return false;
	return !sessionStore.can(option.permission_create);
};

function onSelectTag(event: ITag[]) {
	form.tags = event.map((tag) => tag.tag_id);
}

function getFirstSentence(text) {
	if (!text || typeof text !== "string") return "";

	const match = text.match(/.*?([.!?]|$|\n)/);

	if (match) {
		let sentence = match[0].trim();

		if (sentence.endsWith(".") || sentence.endsWith("!")) {
			sentence = sentence.slice(0, -1).trim();
		}

		return sentence;
	}

	return "";
}

function onEditorTextChange(event: any) {
	const quillInstance = event.instance; // Get the Quill instance
	const textContent = quillInstance.getText().trim();
	firstSentence.value = getFirstSentence(textContent);
}
</script>

<template>
	<div class="create-post-wrapper admin-section-outer-wrapper">
		<div class="admin-section-inner-wrapper">
			<div class="admin-section-header-wrapper">
				<div class="admin-section-header-heading">
					{{ $t("app.createPost.title") }}
				</div>
			</div>
			<div class="admin-section-content-wrapper">
				<div class="admin-section-content-input-wrapper">
					<label
						for="title"
						class="admin-section-content-input-label"
					>
						{{ $t("app.postForm.title") }}
					</label>
					<InputText
						id="title"
						v-model="form.pst_title"
						maxlength="255"
					/>
				</div>
				<div class="admin-section-content-input-wrapper">
					<label
						for="description"
						class="admin-section-content-input-label"
					>
						{{ $t("app.postForm.metaDescription") }}
					</label>
					<InputText
						id="description"
						v-model="form.pst_description"
						maxlength="255"
					/>
				</div>
				<div class="admin-section-content-form-group-wrapper">
					<div class="admin-section-content-form-group-left">
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="datepicker-24h"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.postDate") }}
							</label>
							<DatePicker
								id="datepicker-24h"
								v-model="form.pst_utc_datetime"
								showTime
								hourFormat="24"
								fluid
								dateFormat="dd.mm.yy."
								showIcon
								iconDisplay="input"
							/>
						</div>
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="status"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.status") }}
							</label>
							<Select
								id="status"
								v-model="form.sta_code"
								:options="store.statuses"
								optionLabel="name"
								:optionDisabled="isOptionDisabled"
								fluid
							/>
						</div>
					</div>

					<div class="admin-section-content-form-group-left">
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="tags"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.tags.label") }}
							</label>
							<MultiSelect
								display="chip"
								:options="store.tagsList"
								optionLabel="tag_label"
								filter
								fluid
								:placeholder="$t('app.postForm.tags.placeholder')"
								:maxSelectedLabels="10"
								:showClear="true"
								:resetFilterOnClear="true"
								:showToggleAll="true"
								:virtualScrollerOptions="{ itemSize: 36 }"
								@update:modelValue="onSelectTag($event)"
							/>
						</div>

						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="poster-name"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.posterName") }}
							</label>
							<InputText
								id="poster-name"
								v-model="form.poster_name"
								maxlength="255"
								fluid
							/>
						</div>
					</div>
				</div>
				<div class="admin-section-content-form-group-wrapper">
					<div class="admin-section-content-form-group-center">
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="poster"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.poster") }}
							</label>
							<FileUpload
								ref="posterUploadRef"
								name="poster"
								accept="image/*"
								:maxFileSize="5243000"
								:showUploadButton="false"
								:customUpload="true"
								:fileLimit="1"
								@select="onImageSelect($event, 'poster')"
								@clear="onImageClear('poster')"
								@remove="onImageClear('poster')"
								:chooseLabel="$t('app.basic.choose')"
								:cancelLabel="$t('app.basic.cancel')"
								:pt="{
									root: {
										style: 'margin-bottom: 15px',
									},
								}"
							>
								<template #empty>
									<span v-if="!store.isMobileSize">
										{{ $t("app.postForm.dragAndDrop") }}
									</span>
								</template>
							</FileUpload>
							<div
								v-if="postPosterUrl"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.preview") }}
							</div>
							<Image
								v-if="postPosterUrl"
								:src="postPosterUrl"
								:alt="$t('app.postForm.poster')"
								:pt="{
									root: {
										style: `width: 100%;
												aspect-ratio: 100 / 55.55;`,
									},
									image: {
										style: `width: 100%;
												aspect-ratio: 100 / 55.55;
												object-fit: cover;`,
									},
								}"
							/>
						</div>
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="thumbnail"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.thumbnail") }}
							</label>
							<FileUpload
								ref="thumbnailUploadRef"
								name="thumbnail"
								accept="image/*"
								:fileLimit="1"
								:maxFileSize="5243000"
								:showUploadButton="false"
								@select="onImageSelect($event, 'thumbnail')"
								@clear="onImageClear('thumbnail')"
								@remove="onImageClear('thumbnail')"
								:chooseLabel="$t('app.basic.choose')"
								:cancelLabel="$t('app.basic.cancel')"
								:pt="{
									root: {
										style: 'margin-bottom: 15px',
									},
								}"
							>
								<template #empty>
									<span v-if="!store.isMobileSize">
										{{ $t("app.postForm.dragAndDrop") }}
									</span>
								</template>
							</FileUpload>
							<div
								v-if="postThumbnailUrl"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.preview") }}
							</div>
							<Image
								v-if="postThumbnailUrl"
								:src="postThumbnailUrl"
								:alt="$t('app.postForm.thumbnail')"
								:pt="{
									root: {
										style: `width: 100%;
												aspect-ratio: 100 / 55.55;`,
									},
									image: {
										style: `width: 100%;
												aspect-ratio: 100 / 55.55;
												object-fit: cover;`,
									},
								}"
							/>
						</div>
					</div>
				</div>
				<div class="admin-section-content-input-wrapper">
					<label
						for="content"
						class="admin-section-content-input-label"
					>
						{{ $t("app.postForm.content") }}*
					</label>
					<Editor
						v-model="form.pst_content"
						contentType="html"
						@text-change="onEditorTextChange"
					/>
				</div>
				<div class="admin-section-content-submit-wrapper">
					<Button
						:label="$t('app.basic.clear')"
						severity="secondary"
						:pt="{
							root: {
								class: 'admin-section-content-submit-button',
							},
						}"
						@click="clearForm()"
					/>
					<Button
						class="admin-section-content-submit-button"
						:label="$t('app.basic.submit')"
						:loading="submitLoading"
						@click="createPost()"
						:disabled="!isValidLoginForm"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.p-fileupload-file-badge {
	display: none !important;
}
.p-progressbar-determinate {
	display: none !important;
}
</style>
